import * as React from "react";
import { useMutation } from "convex/react";
import { toast } from "sonner";
import { AlertTriangle, Check } from "lucide-react";
import { api } from "$convex/convexApi";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

type DeleteFolderDialogProps = {
  authUserId: string;
  folderPath: string;
  folderName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
};

export function DeleteFolderDialog({
  authUserId,
  folderPath,
  folderName,
  open,
  onOpenChange,
  onSuccess,
}: DeleteFolderDialogProps) {
  const [deleteDocuments, setDeleteDocuments] = React.useState<"archive" | "delete">("archive");
  const [isDeleting, setIsDeleting] = React.useState(false);

  const deleteFolder = useMutation(api.knowledgeBase.deleteFolder);

  const handleDelete = React.useCallback(async () => {
    setIsDeleting(true);

    try {
      const result = await deleteFolder({
        authUserId,
        folderPath,
        deleteDocuments: deleteDocuments === "delete",
      });

      toast.success(result.message);
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to delete folder";
      toast.error(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  }, [authUserId, deleteDocuments, deleteFolder, folderPath, onOpenChange, onSuccess]);

  React.useEffect(() => {
    if (!open) {
      setDeleteDocuments("archive");
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="size-5" />
            Delete Folder
          </DialogTitle>
          <DialogDescription>
            Are you sure you want to delete "{folderName}"?
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-3">
            <Label>What should happen to documents in this folder?</Label>
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => setDeleteDocuments("archive")}
                className={cn(
                  "flex w-full items-center gap-3 rounded-md border p-3 text-left transition-colors",
                  deleteDocuments === "archive"
                    ? "border-primary bg-primary/5"
                    : "border-border hover:bg-accent",
                )}
              >
                <div
                  className={cn(
                    "flex size-5 items-center justify-center rounded-full border-2",
                    deleteDocuments === "archive"
                      ? "border-primary bg-primary"
                      : "border-muted-foreground",
                  )}
                >
                  {deleteDocuments === "archive" ? (
                    <Check className="size-3 text-primary-foreground" />
                  ) : null}
                </div>
                <div className="flex-1">
                  <div className="font-medium">Archive documents (recommended)</div>
                  <div className="text-xs text-muted-foreground">
                    Documents will be archived and can be restored later
                  </div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setDeleteDocuments("delete")}
                className={cn(
                  "flex w-full items-center gap-3 rounded-md border p-3 text-left transition-colors",
                  deleteDocuments === "delete"
                    ? "border-destructive bg-destructive/5"
                    : "border-border hover:bg-accent",
                )}
              >
                <div
                  className={cn(
                    "flex size-5 items-center justify-center rounded-full border-2",
                    deleteDocuments === "delete"
                      ? "border-destructive bg-destructive"
                      : "border-muted-foreground",
                  )}
                >
                  {deleteDocuments === "delete" ? (
                    <Check className="size-3 text-destructive-foreground" />
                  ) : null}
                </div>
                <div className="flex-1">
                  <div className="font-medium text-destructive">Permanently delete documents</div>
                  <div className="text-xs text-muted-foreground">
                    This action cannot be undone. All documents will be permanently deleted.
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Delete Folder"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
